import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import Seo from "@/components/site/Seo";
import Band, { Eyebrow } from "@/components/site/Band";
import { usePartners } from "@/hooks/useSiteData";
import { OG, US_STATES, SERVED_STATES } from "@/lib/content";

const MarketCheck = () => {
  const [state, setState] = useState("");
  const navigate = useNavigate();
  const served = state && SERVED_STATES.includes(state);
  const chosen = state && !served;

  return (
    <div className="border border-sage/40 rounded-[2px] p-6 sm:p-8 max-w-xl bg-spruce" data-testid="market-check">
      <label htmlFor="market-select" className="block text-sm font-medium text-bone mb-2">
        Where is your practice?
      </label>
      <select
        id="market-select"
        value={state}
        onChange={(e) => setState(e.target.value)}
        className="w-full bg-spruce border border-sage/50 rounded-[2px] px-3 py-2.5 text-sm text-bone focus:border-brass outline-none"
        data-testid="market-select"
      >
        <option value="">Select your state…</option>
        {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
      </select>

      {served && (
        <div className="mt-5" data-testid="market-served">
          <p className="text-bone text-sm flex items-start gap-2 mb-4">
            <Check size={18} className="text-brass shrink-0 mt-0.5" />
            We're active in your state through a covered-entity partner. Let's talk about your practice.
          </p>
          <button
            onClick={() => navigate("/contact", { state: { path: "general", market: state } })}
            className="inline-flex items-center gap-2 text-sm font-semibold text-spruce bg-brass px-5 py-3 rounded-[2px] hover:brightness-95 transition-all"
            data-testid="market-served-cta"
          >
            Start the conversation <ArrowRight size={16} />
          </button>
        </div>
      )}

      {chosen && (
        <div className="mt-5" data-testid="market-waitlist">
          <p className="text-sage text-sm mb-4">
            We're not in your market yet — we'd rather tell you that here than on a call. We're expanding
            through additional covered entities. Join the waitlist and you'll be first to know when we reach you.
          </p>
          <button
            onClick={() => navigate("/contact", { state: { path: "general", market: state } })}
            className="inline-flex items-center gap-2 text-sm font-semibold text-spruce bg-brass px-5 py-3 rounded-[2px] hover:brightness-95 transition-all"
            data-testid="market-waitlist-cta"
          >
            Join the {US_STATES.find(([c]) => c === state)?.[1]} waitlist <ArrowRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default function Network() {
  const { data: partners } = usePartners();

  return (
    <>
      <Seo
        title="The hospital network behind the programs"
        description="Health and More works through named hospital-system covered entities: Tenor Health Foundation in Pennsylvania and Insight Hospital in Chicago, with ElevaRx care coordination. See the geography before you call."
        path="/network"
        ogImage={OG.network}
      />

      <Band tone="spruce">
        <Eyebrow>The network</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mb-6 max-w-3xl">
          The moat is the covered entities we can name.
        </h1>
        <p className="text-bone/85 text-base sm:text-lg max-w-2xl">
          340B expertise is easy to claim. Naming the hospital systems you actually work through is not.
          These are ours, with the geography stated honestly so you don't spend a phone call finding out
          you're outside the map.
        </p>
      </Band>

      {(partners || []).map((p, idx) => (
        <Band key={p.id} tone={idx % 2 === 0 ? "bone" : "ink"} data-testid={`network-partner-${p.id}`}>
          <div className="grid lg:grid-cols-[minmax(0,20rem)_1fr] gap-8 lg:gap-16">
            <div>
              <div className={`tabular text-xs uppercase tracking-wider mb-2 ${idx % 2 === 0 ? "text-sage" : "text-sage"}`}>
                {p.type}
              </div>
              <h2 className={`font-display text-3xl sm:text-4xl leading-tight mb-2 ${idx % 2 === 0 ? "text-spruce" : "text-bone"}`}>
                {p.name}
              </h2>
              <div className="tabular text-sm text-brass mb-4">{p.geography}</div>
              {p.url && (
                <a href={p.url} target="_blank" rel="noopener noreferrer"
                   className={`inline-flex items-center gap-1 text-sm ${idx % 2 === 0 ? "text-spruce" : "text-bone"} hover:text-brass transition-colors`}
                   data-testid={`network-link-${p.id}`}>
                  Visit site <ArrowUpRight size={14} />
                </a>
              )}
              {p.hospitals && p.hospitals.length > 0 && (
                <ul className="mt-5 space-y-1.5">
                  {p.hospitals.map((h) => (
                    <li key={h} className={`text-sm flex gap-2 ${idx % 2 === 0 ? "text-ink/80" : "text-sage"}`}>
                      <span className="text-brass">—</span>{h}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={`space-y-6 ${idx % 2 === 0 ? "text-ink/85" : "text-sage"}`}>
              <p className="text-base leading-relaxed">{p.summary}</p>
              <div>
                <div className={`text-sm font-semibold mb-1 ${idx % 2 === 0 ? "text-spruce" : "text-bone"}`}>
                  What participation means for you
                </div>
                <p className="text-sm leading-relaxed">{p.for_physician}</p>
              </div>
              <div>
                <div className={`text-sm font-semibold mb-1 ${idx % 2 === 0 ? "text-spruce" : "text-bone"}`}>
                  Why the hospital partners with us
                </div>
                <p className="text-sm leading-relaxed">{p.why_partner}</p>
              </div>
            </div>
          </div>
        </Band>
      ))}

      {/* Coverage honesty + waitlist */}
      <Band tone="spruce">
        <Eyebrow>Geographic coverage</Eyebrow>
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-4">
            Right now we serve Pennsylvania and Chicago.
          </h2>
          <p className="text-bone/85 text-base mb-4">
            If your practice sits outside these markets, we'd rather you learn that here than after a
            phone call. We're expanding through additional covered entities, and we keep a waitlist by
            market and specialty.
          </p>
          <p className="text-bone/85 text-base mb-8">
            Tell us where you are and what you practice. If we're coming to your market, you'll be first
            to know.
          </p>
          <MarketCheck />
        </div>
      </Band>
    </>
  );
}
